import { useEffect, useState } from 'react'
import { ArrowUpRight, Info, MousePointerClick, Nfc, Tag } from 'lucide-react'
import { Link } from '../router.js'
import { apiGet } from '../admin/primitives.js'
import { LiveNumber, QuietNumber } from '../soirees/lib.js'
import { useInstagram, metrique } from './InstagramData.js'
import { InsightsChart } from './InstagramChart.js'
import { useCommunication } from './data.js'
import { estErreurApi, nombreFr, type CommunicationPeriod } from './lib.js'

/**
 * ACQUISITION CLUB — les widgets Communication du tracker (V1.1).
 *
 * RÈGLE PRODUIT (contrat §14) : deux blocs, deux sources NOMMÉES. Les clics
 * du tracker et l'activité Meta se REGARDENT côte à côte sur la même
 * période civile — jamais fusionnés, jamais un « taux de conversion ».
 *
 * SÉMANTIQUE (V1.1 §22-23) : « X liens à classifier » compte des LIENS ;
 * « Non classés » dans la ventilation compte des CLICS. Deux concepts,
 * deux chiffres — jamais « 35 supports » quand ce sont 35 clics.
 */

interface OverviewAcq {
  configured: boolean
  health: { status: string }
  last_sync_ok_at: string | null
  stats: {
    has_data: boolean
    clicks: number
    visitors: number
    prev_clicks: number | null
    by_source: Array<{ source: string | null; clicks: number; visitors: number }>
    daily: Array<{ day: string; clicks: number; visitors: number }>
    bots_ecartes: number
  }
  unclassified_links: number
  source_labels: Record<string, string>
}

function useAcquisition(periode: CommunicationPeriod): OverviewAcq | null {
  const [data, setData] = useState<OverviewAcq | null>(null)
  useEffect(() => {
    void apiGet<OverviewAcq | { error: string }>(`/api/v1/communication/acquisition/overview?period=${periode}`)
      .then((j) => { if (!estErreurApi(j)) setData(j as OverviewAcq) })
      .catch(() => { /* le widget affichera l'état honnête */ })
  }, [periode])
  return data
}

const TOOLTIP_COMPARAISON =
  'Les clics sont mesurés par le tracker avant l’ouverture d’Instagram. '
  + 'Les visites du profil, le reach et les abonnements proviennent de Meta. '
  + 'Les séries sont mises en regard sur la même période mais ne constituent '
  + 'pas une attribution individuelle.'

/** §71 : avant le premier événement, « pas de données » — jamais un 0 inventé. */
function PasDeDonnees() {
  return <p className="lc-hint">Pas de données de tracking pour l’instant — la première synchronisation les fera apparaître.</p>
}

/** Delta discret du système existant — une baisse n'est pas une alarme. */
function DeltaAcq({ current, prev }: { current: number; prev: number | null }) {
  if (prev === null || prev === 0) return null
  const abs = current - prev
  const pct = (abs / prev) * 100
  return (
    <span className={`ig-delta ig-delta--discret ${abs >= 0 ? 'ig-delta--up' : 'ig-delta--down'}`}>
      {abs >= 0 ? '+' : ''}{nombreFr(abs)}
      <em>{abs >= 0 ? '+' : ''}{pct.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} % vs période précédente</em>
    </span>
  )
}

/* ═══════ Widget page Instagram : TRAFIC DES SUPPORTS DU CLUB ═══════ */

export function IgAcquisitionWidget() {
  const { data: ig, periode } = useInstagram()
  const acq = useAcquisition(periode)
  const visites = metrique(ig, 'profile_views')
  const reach = metrique(ig, 'reach')

  const sourcesTriees = acq?.stats.by_source ?? []

  return (
    <div className="lc-card lc-card--cyan cm-widget acqw2">
      <span className="lc-card__edge" aria-hidden="true" />
      <header className="cm-widget__head acqw2__head">
        <span className="lc-card__icon" aria-hidden="true"><Nfc /></span>
        <div className="acqw2__titre">
          <h3>Trafic des supports du club</h3>
          <span className="cm-widget__sub">
            QR Codes · NFC · Web · Email — source : insta.liveclubrennes.com
          </span>
        </div>
        {/* CTA principal : dit ce qu'il ouvre, et l'ouvre vraiment (§18-20). */}
        <Link href="/communication/liens" className="lc-btn acqw2__cta">
          Voir le suivi <ArrowUpRight size={14} aria-hidden="true" />
        </Link>
      </header>

      {!acq || !acq.configured || !acq.stats.has_data ? <PasDeDonnees /> : (
        <div className="acqw2__corps">
          <div className="acqw2__gauche">
            <div className="acqw2__kpis">
              <div className="acqw2__kpi">
                <strong className="cm-big__num"><LiveNumber value={acq.stats.clicks} format={nombreFr} /></strong>
                <span className="cm-big__label">clics</span>
              </div>
              <div className="acqw2__kpi">
                <strong className="acqw2__kpi2"><LiveNumber value={acq.stats.visitors} format={nombreFr} /></strong>
                <span className="cm-big__label">navigateurs uniques</span>
              </div>
              <DeltaAcq current={acq.stats.clicks} prev={acq.stats.prev_clicks} />
            </div>

            {/* Mini courbe RÉELLE — événements locaux, jours civils, vides à 0. */}
            {acq.stats.daily.some((d) => d.clicks > 0) ? (
              <div className="acqw2__spark" aria-hidden="true">
                <InsightsChart points={acq.stats.daily.map((d) => ({ day: d.day, value: d.clicks }))} height={64} accent="var(--cyan)" />
              </div>
            ) : null}

            {/* Ventilation compacte par source — des CLICS (§23). */}
            <div className="acqw2__chips">
              {sourcesTriees.map((s) => (
                <span key={s.source ?? 'null'} className={`acqw2__chip ${s.source === null ? 'acqw2__chip--nc' : ''}`}>
                  {s.source === null ? 'Non classés' : acq.source_labels[s.source] ?? s.source}
                  <strong><QuietNumber value={s.clicks} format={nombreFr} /></strong>
                </span>
              ))}
            </div>

            {acq.unclassified_links > 0 ? (
              <p className="acqw2__classer">
                <Tag size={13} aria-hidden="true" />
                <span>
                  <strong>{acq.unclassified_links} {acq.unclassified_links > 1 ? 'liens' : 'lien'} à classifier</strong>
                  {' '}— leurs clics apparaissent en « Non classés ».
                </span>
                <Link href="/communication/liens?filtre=non-classes" className="lc-btn lc-btn--ghost acqw2__classer-cta">
                  Classifier →
                </Link>
              </p>
            ) : null}
          </div>

          {/* ─── Mise en regard compacte (§32) — sources NOMMÉES, aucune
               attribution individuelle, aucun taux entre colonnes. ─── */}
          <aside className="acqw2__regard" title={TOOLTIP_COMPARAISON}>
            <span className="acqw2__regard-titre">
              SUR LA MÊME PÉRIODE <Info size={11} aria-hidden="true" />
            </span>
            <ul>
              <li>
                <span>Clics des supports <em>tracker club</em></span>
                <strong>{nombreFr(acq.stats.clicks)}</strong>
              </li>
              <li>
                <span>Visites du profil <em>Meta</em></span>
                <strong>{visites?.availability === 'AVAILABLE' && visites.value !== null ? nombreFr(visites.value) : 'non disponible'}</strong>
              </li>
              <li>
                <span>Nouveaux abonnements <em>Meta</em></span>
                <strong>{ig?.follows.gained !== null && ig !== null ? nombreFr(ig.follows.gained) : 'non disponible'}</strong>
              </li>
              <li>
                <span>Comptes atteints <em>Meta</em></span>
                <strong>{reach?.availability === 'AVAILABLE' && reach.value !== null ? nombreFr(reach.value) : 'non disponible'}</strong>
              </li>
            </ul>
            <span className="lc-hint acqw2__regard-note">
              Mise en regard civile — pas une attribution individuelle.
            </span>
          </aside>
        </div>
      )}
    </div>
  )
}

/* ═══════ Tuile home Communication (§79 — légère, masquée par défaut) ═══════ */

export function AcquisitionHomeWidget() {
  const { periode } = useCommunication()
  const acq = useAcquisition(periode)
  return (
    <div className="lc-card lc-card--cyan cm-widget">
      <span className="lc-card__edge" aria-hidden="true" />
      <header className="cm-widget__head">
        <span className="lc-card__icon" aria-hidden="true"><MousePointerClick /></span>
        <div>
          <h3>Acquisition Club</h3>
          <span className="cm-widget__sub">QR · NFC · liens — source : insta.liveclubrennes.com</span>
        </div>
      </header>
      {!acq || !acq.configured || !acq.stats.has_data ? <PasDeDonnees /> : (
        <>
          <div className="cm-big">
            <strong className="cm-big__num"><LiveNumber value={acq.stats.clicks} format={nombreFr} /></strong>
            <span className="cm-big__label">clics</span>
          </div>
          <ul className="cm-actionlist" style={{ marginTop: 'auto' }}>
            <li><span className="cm-actionlist__label">Navigateurs uniques</span><strong><QuietNumber value={acq.stats.visitors} format={nombreFr} /></strong></li>
          </ul>
        </>
      )}
      <Link href="/communication/liens" className="lc-btn lc-btn--ghost acqw-lien">Voir le suivi <ArrowUpRight size={13} aria-hidden="true" /></Link>
    </div>
  )
}
